import React, { useState, useMemo } from 'react';
import { _T, C } from '../../shared/theme';
import { s_GC } from '../../shared/styles';
import { useStorage } from '../../shared/hooks';
import { GROWTH_CATEGORIES } from '../goals/constants';
import { EXERCISES, EXERCISE_GROWTH_MAP } from '../exercises/constants';
import { GAME_DEFS } from '../games/constants';

// Progress toward next level (same logic as GoalsScreen)
function getProgress(catId, currentLevel, exHistory) {
  if (currentLevel >= 5) return { pct: 100, sessionsLeft: 0, daysLeft: 0, sessions: 0, sessionsNeeded: 0, daysSpread: 0, daysNeeded: 0 };
  const cat = GROWTH_CATEGORIES.find(c => c.id === catId);
  if (!cat) return { pct: 0, sessionsLeft: 0, daysLeft: 0 };
  const levelDef = cat.levels[currentLevel - 1];
  const windowMs = 30 * 24 * 3600 * 1000;
  const recent = exHistory.filter(h => h.catId === catId && h.level === currentLevel && h.date > Date.now() - windowMs);
  const weighted = recent.reduce((a, h) => a + (h.xp === 3 ? 1.5 : h.xp === 2 ? 1 : 0.5), 0);
  const days = new Set(recent.map(h => new Date(h.date).toDateString())).size;
  const pct = Math.round(((Math.min(1, weighted / levelDef.sessions) + Math.min(1, days / levelDef.days)) / 2) * 100);
  return {
    pct,
    sessions: Math.round(weighted), sessionsNeeded: levelDef.sessions,
    daysSpread: days, daysNeeded: levelDef.days,
    sessionsLeft: Math.max(0, Math.ceil(levelDef.sessions - weighted)),
    daysLeft: Math.max(0, levelDef.days - days),
  };
}

// Get ISO date for a day offset from this week's Monday (0=Mon … 6=Sun)
function getWeekDateForDay(dayIndex) {
  const d = new Date();
  const dow = d.getDay() || 7;
  const mon = new Date(d);
  mon.setDate(d.getDate() - (dow - 1) + dayIndex);
  return mon.toISOString().slice(0, 10);
}

// Get this week's Monday date string
function getThisMonday() {
  return getWeekDateForDay(0);
}

// Build recommended weekly plan based on intention
function buildWeeklyPlan(intention) {
  const itl = (intention || '').toLowerCase();
  const has = (...words) => words.some(w => itl.includes(w));

  const isStress   = has('stress','rustig','calm','ontspan','relax','angst','anxiety','spanning');
  const isFocus    = has('focus','concentr','productief','productiv','werk','work','studie','study','scherpte');
  const isEnergy   = has('energie','energy','vitaal','vital','wakker','actief','active','refresh');
  const isStrength = has('sterk','strong','kracht','fit','sport','power','spier','muscle','train');
  const isSleep    = has('slaap','sleep','rust','rest','moe','tired','vermoeid');
  const isMood     = has('humeur','mood','blij','happy','positief','positive','gevoel','stemming');

  // Each plan item: { type:'exercise'|'game', id, day:0-6 }
  if (isStress) return [
    { type: 'game',     id: 'breathgame', day: 0 },
    { type: 'exercise', id: 'breathing',  day: 2 },
    { type: 'exercise', id: 'meditation', day: 4 },
  ];
  if (isFocus) return [
    { type: 'game',     id: 'stroop',   day: 1 },
    { type: 'exercise', id: 'focus',    day: 3 },
    { type: 'game',     id: 'focustap', day: 5 },
  ];
  if (isEnergy) return [
    { type: 'game',     id: 'reaction', day: 0 },
    { type: 'exercise', id: 'wimhof',   day: 2 },
    { type: 'game',     id: 'focustap', day: 4 },
  ];
  if (isStrength) return [
    { type: 'exercise', id: 'powerpose', day: 0 },
    { type: 'game',     id: 'sequence',  day: 2 },
    { type: 'exercise', id: 'yoga',      day: 4 },
  ];
  if (isSleep) return [
    { type: 'exercise', id: 'meditation', day: 0 },
    { type: 'exercise', id: 'bodyscan',   day: 2 },
    { type: 'game',     id: 'breathgame', day: 4 },
  ];
  if (isMood) return [
    { type: 'exercise', id: 'gratitude', day: 0 },
    { type: 'game',     id: 'memory',    day: 2 },
    { type: 'exercise', id: 'breathing', day: 4 },
  ];
  // default
  return [
    { type: 'exercise', id: 'breathing',  day: 0 },
    { type: 'game',     id: 'memory',     day: 2 },
    { type: 'exercise', id: 'meditation', day: 4 },
  ];
}

const DAY_LABELS_NL = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const DAY_LABELS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function GoalStatsScreen({ lang, weekIntention, onBack, onNav, datedAgenda, setDatedAgenda }) {
  const nl = lang === 'nl';
  const [catLevels] = useStorage('sl_cat_levels', {});
  const [exHistory] = useStorage('sl_ex_history', []);
  const [selCat, setSelCat] = useState(null);

  // Reconstruct unique sessions: one exercise done once → multiple history entries (one per cat)
  // Group by exId + same minute
  const sessionMap = {};
  exHistory.forEach(entry => {
    const key = `${entry.exId}_${Math.floor(entry.date / 60000)}`;
    if (!sessionMap[key]) {
      sessionMap[key] = { exId: entry.exId, date: entry.date, feelings: entry.feelings || [], cats: [] };
    }
    if (!sessionMap[key].cats.includes(entry.catId)) sessionMap[key].cats.push(entry.catId);
  });
  const sessions = Object.values(sessionMap).sort((a, b) => b.date - a.date);
  const filtered = selCat ? sessions.filter(s => s.cats.includes(selCat)) : sessions;

  // Group filtered sessions by calendar day
  const grouped = {};
  filtered.forEach(s => {
    const dayKey = new Date(s.date).toDateString();
    if (!grouped[dayKey]) grouped[dayKey] = [];
    grouped[dayKey].push(s);
  });

  const thisWeekStart = Date.now() - 7 * 24 * 3600 * 1000;

  const formatDayLabel = (ts) => {
    const d = new Date(ts);
    const today = new Date();
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return nl ? 'Vandaag' : 'Today';
    if (d.toDateString() === yesterday.toDateString()) return nl ? 'Gisteren' : 'Yesterday';
    return d.toLocaleDateString(nl ? 'nl-NL' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' });
  };
  const formatTime = (ts) =>
    new Date(ts).toLocaleTimeString(nl ? 'nl-NL' : 'en-US', { hour: '2-digit', minute: '2-digit' });

  // Streak: consecutive days with any exercise
  const allDays = [...new Set(sessions.map(s => new Date(s.date).toDateString()))];
  let streak = 0;
  const now = new Date();
  for (let i = 0; i < 60; i++) {
    const d = new Date(now); d.setDate(d.getDate() - i);
    if (allDays.includes(d.toDateString())) streak++;
    else if (i > 0) break;
  }

  // Weekly plan
  const weekPlan = useMemo(() => {
    if (!weekIntention) return [];
    const raw = buildWeeklyPlan(weekIntention);
    const mondayStr = getThisMonday();
    const weekStartTs = new Date(mondayStr).getTime();
    const weekEndTs   = weekStartTs + 7 * 24 * 3600 * 1000;

    // Exercises done this week (by exId)
    const doneExIds = new Set(
      exHistory.filter(h => h.date >= weekStartTs && h.date < weekEndTs).map(h => h.exId)
    );

    return raw.map(item => {
      const dateStr = getWeekDateForDay(item.day);
      if (item.type === 'exercise') {
        const ex = EXERCISES.find(e => e.id === item.id);
        if (!ex) return null;
        const isDone = doneExIds.has(ex.id);
        const isPlanned = ((datedAgenda || {})[dateStr] || []).some(i => i.exId === ex.id || i.title === (nl ? ex.label : ex.labelEN));
        return { type: 'exercise', id: ex.id, name: nl ? ex.label : ex.labelEN, icon: ex.icon, duration: ex.duration, color: ex.color, day: item.day, dateStr, isDone, isPlanned };
      } else {
        const game = GAME_DEFS.find(g => g.id === item.id);
        if (!game) return null;
        const gameName = nl ? game.labelNL : game.label;
        const isPlanned = ((datedAgenda || {})[dateStr] || []).some(i => i.gameId === game.id || i.title === gameName);
        return { type: 'game', id: game.id, name: gameName, icon: game.icon, duration: 5, color: game.color, day: item.day, dateStr, isDone: false, isPlanned };
      }
    }).filter(Boolean);
  }, [weekIntention, exHistory, datedAgenda, nl]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlan = (item) => {
    const newItem = {
      id: `plan_${Date.now()}_${item.id}`,
      title: item.name,
      icon: item.icon,
      duration: item.duration,
      mental: item.type === 'exercise' ? 20 : 15,
      physical: 10,
      description: '',
      attendees: [],
      ...(item.type === 'exercise' ? { exId: item.id } : { gameId: item.id }),
    };
    setDatedAgenda?.(prev => ({
      ...prev,
      [item.dateStr]: [...((prev || {})[item.dateStr] || []), newItem],
    }));
  };

  const handlePlay = (item) => {
    if (item.type === 'exercise') onNav?.(2);
    else onNav?.(3);
  };

  return (
    <div className="fadeUp" style={{ paddingBottom: 16 }}>
      {/* Back */}
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>
        ← {nl ? 'Terug' : 'Back'}
      </button>

      {/* Header */}
      <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, color: '#EEF3FF', marginBottom: 2 }}>
        {nl ? 'Jouw Groeireizen' : 'Your Growth Journey'}
      </div>
      <div style={{ color: C.silver, fontSize: 13, marginBottom: 14 }}>
        {nl ? 'Voortgang per categorie & oefeningen' : 'Progress per category & exercises'}
      </div>

      {/* Intention banner */}
      {weekIntention && (
        <div style={{ ...s_GC({ position: 'relative', overflow: 'hidden', marginBottom: 14 }) }}>
          <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: `linear-gradient(180deg,${_T.grad1},${_T.grad2})` }} />
          <div style={{ paddingLeft: 12 }}>
            <div style={{ fontSize: 11, color: _T.accent, letterSpacing: 3, fontWeight: 600, marginBottom: 4 }}>
              {nl ? 'INTENTIE DEZE WEEK' : 'THIS WEEK\'S INTENTION'}
            </div>
            <div style={{ color: C.silver2, fontSize: 13, lineHeight: 1.6, fontStyle: 'italic' }}>"{weekIntention}"</div>
          </div>
        </div>
      )}

      {/* Weekly plan */}
      {weekPlan.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: _T.accent, letterSpacing: 2, fontWeight: 600, marginBottom: 10 }}>
            {nl ? 'WEEKPLAN' : 'WEEKLY PLAN'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {weekPlan.map((item) => {
              const dayLabel = nl ? DAY_LABELS_NL[item.day] : DAY_LABELS_EN[item.day];
              return (
                <div key={`${item.type}_${item.id}`} style={{
                  background: _T.bg3,
                  border: `1px solid ${item.isDone ? item.color + '55' : _T.cardBorderBot}`,
                  borderRadius: 14,
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  opacity: item.isDone ? 0.75 : 1,
                }}>
                  {/* Icon */}
                  <div style={{
                    width: 42, height: 42, borderRadius: 12,
                    background: `${item.color}18`, border: `1.5px solid ${item.color}44`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20, flexShrink: 0, position: 'relative',
                  }}>
                    {item.icon}
                    {item.isDone && (
                      <div style={{ position: 'absolute', bottom: -4, right: -4, background: C.teal, borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 700 }}>✓</div>
                    )}
                    {!item.isDone && item.isPlanned && (
                      <div style={{ position: 'absolute', bottom: -4, right: -4, background: _T.accent, borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9 }}>📅</div>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{item.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                      <span style={{ fontSize: 10, color: item.color, background: `${item.color}18`, borderRadius: 8, padding: '2px 7px', fontWeight: 600 }}>{dayLabel}</span>
                      <span style={{ fontSize: 10, color: C.silver }}>{item.duration} min</span>
                      <span style={{ fontSize: 10, color: C.silver, background: _T.bg4, borderRadius: 8, padding: '2px 7px' }}>{item.type === 'game' ? (nl ? 'Spel' : 'Game') : (nl ? 'Oefening' : 'Exercise')}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    {/* Plan button */}
                    {!item.isPlanned && !item.isDone && setDatedAgenda && (
                      <button onClick={() => handlePlan(item)}
                        title={nl ? 'Inplannen' : 'Add to agenda'}
                        style={{ width: 32, height: 32, borderRadius: 9, background: `${_T.accent}15`, border: `1px solid ${_T.accent}44`, color: _T.accent, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                        📅
                      </button>
                    )}
                    {/* Play button */}
                    <button onClick={() => handlePlay(item)}
                      title={nl ? 'Starten' : 'Start now'}
                      style={{ width: 32, height: 32, borderRadius: 9, background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', color: '#0a1220', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                      ▶
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
        {[
          { label: nl ? 'Totaal' : 'Total', val: sessions.length, icon: '🧘', color: _T.accent },
          { label: nl ? 'Deze week' : 'This week', val: sessions.filter(s => s.date > thisWeekStart).length, icon: '📅', color: C.teal },
          { label: nl ? 'Streak' : 'Streak', val: `${streak}d`, icon: '🔥', color: '#D4873A' },
        ].map(c => (
          <div key={c.label} style={s_GC({ padding: '10px 12px', marginBottom: 0, textAlign: 'center' })}>
            <div style={{ fontSize: 18, marginBottom: 2 }}>{c.icon}</div>
            <div style={{ fontSize: 18, color: c.color, fontWeight: 700 }}>{c.val}</div>
            <div style={{ fontSize: 9, color: C.silver, marginTop: 1 }}>{c.label.toUpperCase()}</div>
          </div>
        ))}
      </div>

      {/* Growth category cards */}
      <div style={{ fontSize: 13, color: _T.accent, letterSpacing: 2, fontWeight: 600, marginBottom: 10 }}>
        {nl ? 'GROEI CATEGORIEËN' : 'GROWTH CATEGORIES'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
        {GROWTH_CATEGORIES.map(cat => {
          const lv = catLevels[cat.id] || 1;
          const prog = getProgress(cat.id, lv, exHistory);
          const weekEntries = exHistory.filter(h => h.catId === cat.id && h.date > thisWeekStart);
          const weekUnique = new Set(weekEntries.map(h => `${h.exId}_${Math.floor(h.date / 60000)}`)).size;
          const totalUnique = new Set(exHistory.filter(h => h.catId === cat.id).map(h => `${h.exId}_${Math.floor(h.date / 60000)}`)).size;
          const isSelected = selCat === cat.id;

          return (
            <div
              key={cat.id}
              onClick={() => setSelCat(isSelected ? null : cat.id)}
              style={{
                background: isSelected ? `${cat.color}18` : _T.bg4,
                borderRadius: 14, padding: '12px 14px',
                border: `1.5px solid ${isSelected ? cat.color : cat.color + '22'}`,
                cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 20 }}>{cat.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: cat.color, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {nl ? cat.label : cat.labelEN}
                  </div>
                  <div style={{ fontSize: 9, color: C.silver }}>
                    Lv {lv}{lv < 5 ? ` → Lv ${lv + 1}` : ' ★ MAX'}
                  </div>
                </div>
                {weekUnique > 0 && (
                  <div style={{ background: `${cat.color}20`, borderRadius: 10, padding: '2px 7px', fontSize: 9, color: cat.color, fontWeight: 700, flexShrink: 0 }}>
                    {weekUnique}×
                  </div>
                )}
              </div>

              {/* Progress bar */}
              <div style={{ height: 4, background: _T.bg3, borderRadius: 2, overflow: 'hidden', marginBottom: 5 }}>
                <div style={{ width: `${prog.pct}%`, height: '100%', background: `linear-gradient(90deg,${cat.color}88,${cat.color})`, borderRadius: 2, transition: 'width 0.6s' }} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 9, color: C.silver }}>{prog.pct}%</span>
                {lv < 5
                  ? <span style={{ fontSize: 9, color: C.silver }}>{prog.sessionsLeft} {nl ? 'sessies nodig' : 'sessions left'}</span>
                  : <span style={{ fontSize: 9, color: cat.color }}>★ {nl ? 'Gemeisterd' : 'Mastered'}</span>
                }
              </div>

              {totalUnique > 0 && (
                <div style={{ marginTop: 5, fontSize: 9, color: C.silver }}>
                  {totalUnique} {nl ? 'sessies totaal' : 'sessions total'}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Exercise timeline */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 13, color: _T.accent, letterSpacing: 2, fontWeight: 600 }}>
          {selCat
            ? `${(GROWTH_CATEGORIES.find(c => c.id === selCat)?.icon || '')} ${nl ? GROWTH_CATEGORIES.find(c => c.id === selCat)?.label : GROWTH_CATEGORIES.find(c => c.id === selCat)?.labelEN}`
            : (nl ? 'ALLE OEFENINGEN' : 'ALL EXERCISES')
          }
        </div>
        {selCat && (
          <button onClick={() => setSelCat(null)} style={{ background: 'none', border: 'none', color: C.silver, fontSize: 11, cursor: 'pointer' }}>
            ✕ {nl ? 'Wis filter' : 'Clear'}
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 36 }), color: C.silver, fontSize: 13 }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🧘</div>
          {selCat
            ? (nl ? 'Geen oefeningen in deze categorie.' : 'No exercises in this category yet.')
            : (nl ? 'Nog geen oefeningen gedaan.' : 'No exercises completed yet.')}
          <div style={{ fontSize: 11, color: C.silver, marginTop: 6 }}>
            {nl ? 'Start een oefening om je voortgang bij te houden!' : 'Start an exercise to track your progress!'}
          </div>
        </div>
      ) : (
        Object.entries(grouped).map(([dayKey, daySessions]) => (
          <div key={dayKey} style={{ marginBottom: 20 }}>
            {/* Day divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: C.silver, fontWeight: 600, letterSpacing: 1 }}>
                {formatDayLabel(daySessions[0].date).toUpperCase()}
              </div>
              <div style={{ flex: 1, height: 1, background: _T.cardBorderBot }} />
              <div style={{ fontSize: 10, color: C.silver, background: _T.bg4, borderRadius: 8, padding: '2px 8px' }}>
                {daySessions.length} {nl ? 'sessie' : 'session'}{daySessions.length > 1 ? 's' : ''}
              </div>
            </div>

            {daySessions.map((s, i) => {
              const ex = EXERCISES.find(e => e.id === s.exId);
              if (!ex) return null;
              const cats = s.cats.map(cid => GROWTH_CATEGORIES.find(c => c.id === cid)).filter(Boolean);
              const growthMap = EXERCISE_GROWTH_MAP[s.exId] || {};

              return (
                <div key={i} style={s_GC({ padding: '12px 14px', marginBottom: 8 })}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    {/* Exercise icon */}
                    <div style={{
                      width: 44, height: 44, borderRadius: 13,
                      background: `${ex.color}18`, border: `1.5px solid ${ex.color}44`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22, flexShrink: 0,
                    }}>
                      {ex.icon}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Name + time */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <div>
                          <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{nl ? ex.label : ex.labelEN}</div>
                          <div style={{ fontSize: 10, color: C.silver }}>{ex.duration} min</div>
                        </div>
                        <div style={{ fontSize: 11, color: C.silver, flexShrink: 0, marginLeft: 8 }}>{formatTime(s.date)}</div>
                      </div>

                      {/* Growth category chips with XP strength */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                        {cats.map(cat => {
                          const xp = growthMap[cat.id] || 1;
                          const bar = xp === 3 ? '▓▓▓' : xp === 2 ? '▓▓░' : '▓░░';
                          return (
                            <span key={cat.id} style={{
                              fontSize: 9, background: `${cat.color}15`,
                              border: `1px solid ${cat.color}44`, color: cat.color,
                              borderRadius: 10, padding: '2px 8px', fontWeight: 600,
                              display: 'flex', alignItems: 'center', gap: 3,
                            }}>
                              {cat.icon} {nl ? cat.label : cat.labelEN} <span style={{ opacity: 0.7 }}>{bar}</span>
                            </span>
                          );
                        })}
                      </div>

                      {/* Feelings tags */}
                      {s.feelings?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {s.feelings.map((f, fi) => (
                            <span key={fi} style={{
                              fontSize: 10, background: _T.bg4,
                              border: `1px solid ${_T.cardBorderBot}`,
                              color: C.silver2, borderRadius: 8, padding: '2px 8px',
                            }}>
                              {f}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}

export default GoalStatsScreen;
